import React, { useId } from 'react';
import { Users, Map as MapIcon, Calendar, Clock, Zap } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

// Presentational filter controls shared by the Explore page and an operator's
// public profile page, so both stay visually and behaviorally in sync.

export const CapacitySelect = ({ value, onChange, className = '' }) => (
  <div className={`position-relative h-100 ${className}`}>
    <Users className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted pe-none" size={18} />
    <select
      className="form-select form-select-lg border-0 bg-light ps-5 rounded-pill text-dark shadow-sm h-100"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="all">Any Field Size</option>
      <option value="5v5">5v5</option>
      <option value="7v7">7v7</option>
      <option value="9v9">9v9</option>
      <option value="11v11">11v11</option>
    </select>
  </div>
);

export const SurfaceSelect = ({ value, onChange, className = '' }) => (
  <div className={`position-relative h-100 ${className}`}>
    <MapIcon className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted pe-none" size={18} />
    <select
      className="form-select form-select-lg border-0 bg-light ps-5 rounded-pill text-dark shadow-sm h-100"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="all">Any Surface</option>
      <option value="Natural Grass">Natural Grass</option>
      <option value="Artificial Turf">Artificial Turf</option>
      <option value="Indoor">Indoor Court</option>
    </select>
  </div>
);

export const DateTimeInput = ({ value, onChange, className = '' }) => (
  <div className={`position-relative d-flex align-items-center h-100 ${className}`}>
    <Calendar className="position-absolute ms-3 text-muted z-1" size={18} />
    <input
      type="datetime-local"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="form-control form-control-lg border-0 bg-light ps-5 rounded-pill text-dark w-100 shadow-sm"
    />
  </div>
);

export const PlayNowButton = ({ active, onClick, className = '' }) => (
  <button
    type="button"
    className={`btn rounded-pill d-flex align-items-center justify-content-center gap-2 transition-hover shadow-sm ${className}`}
    style={{
      backgroundColor: active ? 'var(--field-green)' : 'var(--bg-surface-alt)',
      color: active ? 'white' : 'var(--field-green)',
      border: active ? '1px solid var(--field-green)' : '1px solid transparent',
    }}
    onClick={onClick}
  >
    <Clock size={16} /> Play Now
  </button>
);

export const BookableOnlyToggle = ({ active, onClick, className = '' }) => {
  const switchId = useId();
  const isMobile = useIsMobile();

  if (!isMobile) {
    return (
      <button
        type="button"
        className={`btn btn-lg rounded-pill d-flex align-items-center justify-content-center gap-2 shadow-sm ${className}`}
        style={{
          backgroundColor: active ? 'var(--field-green)' : 'var(--bg-surface-alt)',
          color: active ? 'white' : 'var(--field-green)',
          border: active ? '1px solid var(--field-green)' : '1px solid transparent',
          fontSize: '0.9rem',
          whiteSpace: 'nowrap',
        }}
        onClick={onClick}
        title="Only show fields you can book instantly on Fielder"
      >
        <Zap size={16} /> Bookable on Fielder
      </button>
    );
  }

  // Mobile: Bootstrap's standard form-check/form-switch pattern, with the checked
  // state tinted to the brand color like the rest of this file's active states.
  return (
    <div className={`bg-light rounded-3 d-flex align-items-center justify-content-between px-3 py-2 ${className}`}>
      <label htmlFor={switchId} className="d-flex align-items-center gap-2 text-dark mb-0" style={{ fontSize: '0.9rem', cursor: 'pointer' }}>
        <Zap size={16} style={{ color: 'var(--field-green)' }} />
        Bookable on Fielder
      </label>
      <div className="form-check form-switch mb-0">
        <input
          id={switchId}
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={active}
          onChange={onClick}
          style={active ? { backgroundColor: 'var(--field-green)', borderColor: 'var(--field-green)', cursor: 'pointer' } : { cursor: 'pointer' }}
        />
      </div>
    </div>
  );
};
